import { ParsedUrlQuery } from 'querystring'

import { getCurrentBaseUrl } from '@hhgtech/hhg-components'
import { GetServerSidePropsResult } from 'next'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useRouter } from 'next/router'

import { callApiNew, callApiNewTest } from 'api/axios'
import { cleanUnicodeUrl } from 'api/utils/paths'
import {
  handleRedirection,
  verifyNotFoundToRedirect,
} from 'api/utils/redirects'
import { checkMobile } from 'api/utils/validators'
import type { Props as ArticlePageProps } from 'components/pages/article'
import { getServerSideProps as getArticleServerSideProps } from 'components/pages/article/getServerSideProps'
import { getI18nServerMessage } from 'components/pages/article/utilsV2/getI18nServerMessage'
import type { Props as CategoryPageProps } from 'components/pages/category'
import { getServerSideProps as getCategoryServerSideProps } from 'components/pages/category/getServerSideProps'
import type { Props as DrugsPageComponentProps } from 'components/pages/drugs'
import { getServerSideProps as getDrugsPageServerSideProps } from 'components/pages/drugs/getServerSideProps'
import type { Props as DynamicSpotlightPageProps } from 'components/pages/dynamicSpotlight'
import { getServerSideProps as getSpotlightPageServerSideProps } from 'components/pages/spotlight/getServerSideProps'
import { getServerSideProps as getVideoPageServerSideProps } from 'components/pages/videos/getServerSideProps'
import { Props as VideoPageProps } from 'components/pages/videos/index'
import { TEMPLATE_AZ_NAME } from 'interfaces/constants'
import { removeAmpFromUrl } from 'utils/htmlHelper'
import {
  MeasureLog,
  MeasureLogHandler,
  MeasureOptions,
  measureTaskExecTime,
} from 'utils/measureTaskExecTime'

// @ts-ignore
import mergedRemoteCss from '!raw-loader!../styles/merged.css'
import { PATHS } from '../api/paths'
import type {
  ArticleApi,
  DataCategoryApi,
  ExtendedContextType,
  ExtendedGetServerSideProps,
  HealthToolApi,
  PageCategoryApi,
  RedirectionType,
} from '../interfaces/types'

const ArticleV2Page = dynamic(() => import('hhgDiscoverV2/ArticleV2Page'))
const ArticleDemo = dynamic(() => import('hhgDiscoverV2/ArticleDemo'))
const ArticleNoImage = dynamic(() => import('hhgDiscoverV2/ArticleNoImage'))

const DrugsPageComponent = dynamic<DrugsPageComponentProps>(
  () => import('components/pages/drugs'),
)
const VideoPage = dynamic<VideoPageProps>(() =>
  import('components/pages/videos').then((mod) => mod.VideoPage),
)
// const ArticlePageComponent = dynamic<ArticlePageProps>(() =>
//   import('components/pages/article').then((mod) => mod.ArticlePageComponent),
// )
const CategoryPageComponent = dynamic<CategoryPageProps>(() =>
  import('components/pages/category').then((mod) => mod.CategoryPageComponent),
)

const DynamicSpotlightPage = dynamic<DynamicSpotlightPageProps>(() =>
  import('components/pages/dynamicSpotlight').then(
    (mod) => mod.DynamicSpotlightPage,
  ),
)

export type BaseProps = {
  isMobileSsr?: boolean
  page: string
  currentCategory?: string
  ssrRunningTime?: string
  measureLogs?: any[]
}
export type Props =
  | ArticlePageProps
  | CategoryPageProps
  | DrugsPageComponentProps
  | DynamicSpotlightPageProps
  | VideoPageProps

const PostPage = (props: Props) => {
  const { page } = props

  const router = useRouter()

  if (page === 'drugs') {
    return <DrugsPageComponent {...(props as DrugsPageComponentProps)} />
  }

  if (page === 'article') {
    // return <ArticlePageComponent {...(props as ArticlePageProps)} />

    const componentChunkUrl = `${process.env.NEXT_PUBLIC_REMOTE_COMPONENT_URL}/_next/static/chunks/remoteEntry.js`
    const articleChunkUrl = `${process.env.NEXT_PUBLIC_REMOTE_ARTICLE_DETAIL_URL}/_next/static/chunks/remoteEntry.js`

    const {
      articles: _articles_legacy,
      articlesV2,
      sitewideBanner: _sitewideBanner_legacy,
      sitewideBannerV2,
      ...articleProps
    } = props as ArticlePageProps
    const { type } = router.query as { type: 'noimage' | 'demo' | 'empty' }

    console.log('[👀 HHG Tracking] __router.query.type', {
      empty: type === 'empty',
      noimage: type === 'noimage',
      demo: type === 'demo',
    })

    console.log('[👀 HHG Tracking] __article_props', {
      router,
      articles:
        typeof window === 'undefined'
          ? `__HIDDEN__ / (article count: ${articlesV2?.length})`
          : articlesV2,
      isMobileSsr: articleProps.isMobileSsr,
    })

    console.log('[👀 HHG Tracking] __measureLogs', {
      measureLogs: articleProps.measureLogs,
      ssrRunningTime: articleProps.ssrRunningTime,
    })

    if (type === 'empty') {
      return <p>THIS IS EMPTY PAGE</p>
    }

    return (
      <>
        <Head>
          <style
            dangerouslySetInnerHTML={{
              __html: mergedRemoteCss,
            }}
          />
          <link rel="preload" href={componentChunkUrl} as="script" />
          <link rel="preload" href={articleChunkUrl} as="script" />
        </Head>

        {type === 'noimage' ? (
          <ArticleNoImage
            router={router}
            articles={articlesV2}
            sitewideBanner={sitewideBannerV2}
            {...articleProps}
          />
        ) : type === 'demo' ? (
          <ArticleDemo
            router={router}
            articles={articlesV2}
            sitewideBanner={sitewideBannerV2}
            {...articleProps}
          />
        ) : (
          // Load article v2 from hhg-discover-v2
          <ArticleV2Page
            router={router}
            articles={articlesV2}
            sitewideBanner={sitewideBannerV2}
            {...articleProps}
          />
        )}
      </>
    )
  }
  if (page === 'category') {
    return <CategoryPageComponent {...(props as CategoryPageProps)} />
  }
  if (page === 'dynamicSpotlight') {
    return <DynamicSpotlightPage {...(props as DynamicSpotlightPageProps)} />
  }
  if (page === 'videosPage') {
    return <VideoPage {...(props as VideoPageProps)} />
  }
  return null
}

const callArticleOrCategoryApi = async (
  query: ParsedUrlQuery,
  slug?: string | string[],
  measureOptions?: MeasureOptions,
) => {
  const isPreviewPage =
    Array.isArray(slug) && slug.length > 0 && slug[0] === 'preview'
  const fullSlug = Array.isArray(slug) ? slug.join('/') : slug || ''

  if (isPreviewPage) {
    return callApiNew(
      PATHS.ARTICLE.GET_PREVIEW_ARTICLE,
      {
        params: {
          // basePath,
          fullSlug,
          nonce: Array.isArray(query?.preview_nonce)
            ? query?.preview_nonce[0]
            : query?.preview_nonce || '',
        },
      },
      undefined,
      undefined,
      undefined,
      measureOptions,
    )
  }

  if (process.env.NEXT_PUBLIC_DEPLOY_LOCALE === 'tl-PH') {
    const [categoryRes, articleRes] = await Promise.all([
      callApiNew(PATHS.CATEGORY.GET_CATEGORY_DETAIL, {
        params: {
          // basePath,
          slug: fullSlug,
        },
      }),
      callApiNew(PATHS.ARTICLE.GET_ARTICLE_DETAIL, {
        params: {
          // basePath,
          slug: fullSlug,
        },
      }),
    ])

    if (articleRes.status === 1) {
      return articleRes
    }

    if (categoryRes.status === 1) {
      return categoryRes
    }
  } else {
    return callApiNewTest(
      PATHS.CATEGORY.GET_CATEGORY_OR_ARTICLE_DETAIL,
      {
        params: {
          // basePath,
          fullSlug,
        },
      },
      undefined,
      undefined,
      undefined,
      measureOptions,
    )
  }
}

const generateArticleContent = async (
  ctx: ExtendedContextType<ParsedUrlQuery>,
  articleOrCategoryRes?: {
    status: number
    data?: {
      post?: ArticleApi
      category?: DataCategoryApi['category'] & { template?: string }
    } & RedirectionType
  } | null,
): Promise<GetServerSidePropsResult<(BaseProps & Props) | Props>> => {
  if (articleOrCategoryRes) {
    if (articleOrCategoryRes.status === 1) {
      if (articleOrCategoryRes.data?.post) {
        return getArticleServerSideProps(ctx, articleOrCategoryRes)
      } else if (articleOrCategoryRes.data?.category) {
        if (
          articleOrCategoryRes.data?.category?.template === TEMPLATE_AZ_NAME
        ) {
          return getDrugsPageServerSideProps(ctx)
        }

        return getCategoryServerSideProps(
          ctx,
          articleOrCategoryRes as PageCategoryApi,
        )
      }
    } else if (
      articleOrCategoryRes.status === 2 &&
      articleOrCategoryRes.data?.redirect_url
    ) {
      return handleRedirection(
        articleOrCategoryRes.data,
        ctx.query,
      ) as GetServerSidePropsResult<(BaseProps & Props) | Props>
    }
  }
  return {
    notFound: true,
  }
}

export const getServerSideProps: ExtendedGetServerSideProps = async (ctx) => {
  const startMeasure = performance.now()
  let endMeasure = performance.now()

  MeasureLogHandler.reset()
  MeasureLogHandler.push({
    id: null,
    step: null,
    task: 'Begin Measure',
    waitingMs: null,
    moreInfo: {
      startTime: startMeasure,
      endTime: -1,
      parents: null,
    },
  })

  const { query, req, locale = 'en-PH' } = ctx
  const { slug, amp } = query

  const userAgent = `${req.headers['user-agent']}`
  const isMobileSsr = checkMobile(userAgent)
  const joinedSlug = Array.isArray(slug) ? slug.join('/') : slug || ''
  console.log('[👀 HHG Tracking] __slug', joinedSlug)

  if (
    joinedSlug.startsWith('wp-content/uploads/') ||
    joinedSlug.startsWith('apple-touch-icon') ||
    // ignore file extension request, we are serving normal pages
    joinedSlug.endsWith('.php') ||
    joinedSlug.endsWith('.js') ||
    joinedSlug.endsWith('.png') ||
    joinedSlug.endsWith('.jpg')
  ) {
    return {
      notFound: true,
    }
    // return {
    //   redirect: {
    //     destination: `https://cdn.${domainLocales[locale]}/${joinedSlug}`,
    //     statusCode: 301,
    //   },
    // }
  }

  let returnRes: GetServerSidePropsResult<(BaseProps & Props) | Props> = {
    notFound: true,
  }

  if (slug?.[0] === 'videos') {
    returnRes = await getVideoPageServerSideProps(ctx)
  } else {
    const measureLog: MeasureLog = {
      id: 'step_1',
      step: '1',
      task: 'promise_all_1',
      waitingMs: null,
      moreInfo: {
        taskDetails: [
          { task: 'GET_CATEGORY_OR_ARTICLE_DETAIL' },
          { task: 'GET_ALL_HEALTH_TOOLS' },
          { task: 'CHECK_IS_SPOTLIGHT_URL' },
          { task: 'GET_SPOTLIGHT_LANDING' },
        ],
        startTime: performance.now(),
        endTime: -1,
        desc:
          'await Promise.all(articleOrCategory, allHealthTools, checkSpotlight, checkExistSpotlight)',
        parents: null,
      },
    }
    MeasureLogHandler.push(measureLog)

    const [
      [articleOrCategoryRunningTime, articleOrCategoryRes],
      [allHealthToolsRunningTime, allHealthTools],
      [checkSpotlightRunningTime, checkSpotlightRes],
      [checkExistSpotlightRunningTime, checkExistSpotlightRes],
    ] = await Promise.all([
      measureTaskExecTime(() =>
        callArticleOrCategoryApi(query, slug, {
          id: 'step_1',
          task: 'GET_CATEGORY_OR_ARTICLE_DETAIL',
        }),
      ),
      measureTaskExecTime(() =>
        callApiNewTest(
          PATHS.HEALTH_TOOL.GET_ALL_HEALTH_TOOLS,
          undefined,
          undefined,
          undefined,
          undefined,
          { id: 'step_1', task: 'GET_ALL_HEALTH_TOOLS' },
        ),
      ),
      measureTaskExecTime(() =>
        callApiNewTest(
          PATHS.SPOTLIGHT.CHECK_IS_SPOTLIGHT_URL,
          {
            params: {
              url: `${getCurrentBaseUrl(locale)}/${slug?.[0]}`,
            },
          },
          undefined,
          undefined,
          undefined,
          { id: 'step_1', task: 'CHECK_IS_SPOTLIGHT_URL' },
        ),
      ),
      measureTaskExecTime(() =>
        callApiNewTest(
          PATHS.SPOTLIGHT.GET_SPOTLIGHT_LANDING,
          {
            params: {
              slug: slug?.[0],
              ...(slug?.[1] && {
                groupId: slug?.[1],
              }),
              ...(slug?.[2] && {
                additionalSlug: slug?.[2],
              }),
            },
          },
          undefined,
          undefined,
          undefined,
          { id: 'step_1', task: 'GET_SPOTLIGHT_LANDING' },
        ),
      ),
    ])

    MeasureLogHandler.addTrackingTime(measureLog)
    MeasureLogHandler.attachMetadataByTask({
      id: 'step_1',
      task: 'GET_CATEGORY_OR_ARTICLE_DETAIL',
      metadata: { waitingMs: articleOrCategoryRunningTime },
    })
    MeasureLogHandler.attachMetadataByTask({
      id: 'step_1',
      task: 'GET_ALL_HEALTH_TOOLS',
      metadata: { waitingMs: allHealthToolsRunningTime },
    })
    MeasureLogHandler.attachMetadataByTask({
      id: 'step_1',
      task: 'CHECK_IS_SPOTLIGHT_URL',
      metadata: { waitingMs: checkSpotlightRunningTime },
    })
    MeasureLogHandler.attachMetadataByTask({
      id: 'step_1',
      task: 'GET_SPOTLIGHT_LANDING',
      metadata: { waitingMs: checkExistSpotlightRunningTime },
    })

    if (checkSpotlightRes?.data?.type === 'spotlight') {
      // Check if is exist dynamic spotlight
      if (
        checkExistSpotlightRes?.data?.permalink?.search('/spotlight') === -1
      ) {
        returnRes = await getSpotlightPageServerSideProps(
          ctx,
          checkExistSpotlightRes,
        )
      } else if (checkExistSpotlightRes?.data?.redirect_url) {
        return handleRedirection(
          checkExistSpotlightRes.data,
          ctx.query,
        ) as GetServerSidePropsResult<(BaseProps & Props) | Props>
      }
    } else {
      const measureLog2: MeasureLog = {
        id: 'step_2',
        step: '2',
        task: 'generate_article_content',
        waitingMs: null,
        moreInfo: {
          startTime: performance.now(),
          endTime: -1,
          taskDetails: [],
          desc: 'await generateArticleContent(..)',
          parents: null,
        },
      }
      MeasureLogHandler.push(measureLog2)
      returnRes = await generateArticleContent(ctx, articleOrCategoryRes)
      MeasureLogHandler.addTrackingTime(measureLog2)

      // REDIRECT from amp link to non-amp link
      if (ctx.req.url && amp) {
        return {
          redirect: {
            destination: removeAmpFromUrl(ctx.req.url),
            statusCode: 301,
          },
        }
      }

      if ('props' in returnRes) {
        // Remove siteWide banner if has cookie (which means user has disabled it)
        const sitewideBanner = (returnRes.props as Props).sitewideBanner
        const sitewideBannerV2 =
          (returnRes.props as any).sitewideBannerV2 || null

        let cookieValue
        if (sitewideBanner) {
          const cookieName = `discover-disable-sitewide-${sitewideBanner.type}`
          cookieValue = req.cookies[cookieName]
        }

        const measureLog3: MeasureLog = {
          id: 'step_3',
          step: '3',
          task: 'get_i18n_server_message',
          waitingMs: null,
          moreInfo: {
            startTime: performance.now(),
            endTime: -1,
            taskDetails: [],
            desc: 'await getI18nServerMessage(..)',
            parents: null,
          },
        }
        MeasureLogHandler.push(measureLog3)
        const articlePageMessageProps = await getI18nServerMessage(ctx)
        MeasureLogHandler.addTrackingTime(measureLog3)

        endMeasure = performance.now()

        returnRes = {
          ...returnRes,
          props: {
            ...returnRes.props,
            ...articlePageMessageProps.props,
            sitewideBanner: !!cookieValue ? null : sitewideBanner,
            sitewideBannerV2: !!cookieValue ? null : sitewideBannerV2,
            isMobileSsr: !!amp || isMobileSsr,
            measureLogs: MeasureLogHandler.get(),
            ssrRunningTime: `${(endMeasure - startMeasure).toFixed(2)}ms`,
          },
        }
      }

      if ('notFound' in returnRes) {
        returnRes = verifyNotFoundToRedirect(
          slug,
          locale,
        ) as GetServerSidePropsResult<Props | (BaseProps & Props)>
        const allHealthToolLinks = Array.isArray(allHealthTools?.data)
          ? allHealthTools.data?.map((item: HealthToolApi) =>
              item.link.replace(/\//g, ''),
            ) || []
          : []

        if (slug && slug.length > 0) {
          const lastSlug = slug[slug.length - 1]
          if (
            allHealthToolLinks.filter((l: string) => l.indexOf(lastSlug) !== -1)
              .length >= 1
          ) {
            return {
              redirect: {
                destination: cleanUnicodeUrl(`/health-tools/${lastSlug}/`),
                statusCode: 307,
              },
            }
          }
        }

        //CHECK TO REDIRECT PREVIOUS SLUG IN CASE STILL NOT FOUND
        const currentSlug = typeof slug === 'string' ? [slug] : slug
        return {
          redirect: {
            destination: cleanUnicodeUrl(
              `/${currentSlug
                ?.slice(0, currentSlug.slice(0, 5).length - 1)
                .join('/')}/`,
            ),
            statusCode: 307,
          },
        }
      }
    }
  }
  return returnRes
}

export default PostPage
